import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useEmbed } from "@/contexts/EmbedContext";
import { useSocket } from "@/contexts/SocketContext";
import { authFetch } from "@/utils/api";
import { getMyHistory } from "@/api/platformBroadcasts";
import { buildStepConfig } from "./onboardingSteps";

import AuroraBackground from "./components/AuroraBackground";
import AlertPill from "./components/AlertPill";
import Collapse from "./components/Collapse";
import ConversationList from "./components/ConversationList";
import LucasComposer from "./components/LucasComposer";
import LucasRestricted from "./components/LucasRestricted";
import LucasSuggestions from "./components/LucasSuggestions";
import LucasSpark from "./components/LucasSpark";
import OnboardingChecklist from "./components/OnboardingChecklist";
import EditorialCards from "./components/EditorialCards";
import TaskList from "./components/TaskList";
import NoticeDrawer from "./components/NoticeDrawer";
import BillingFooter from "./components/BillingFooter";

import {
  useSupportChatSession,
  useSupportChatUI,
} from "@/contexts/SupportChatContext";

/**
 * O pedaco pesado do Lucas, fora do bundle de entrada.
 *
 * LucasChat arrasta MessageBlocks, LucasMarkdown e com ele o react-markdown.
 * Como esta pagina e importada de forma estatica pelo ImplementationEntry, que
 * e a porta de entrada da maioria, tudo isso viajava no chunk inicial de QUEM
 * NUNCA abre o Lucas. Agora so baixa quando ha conversa para mostrar.
 */
const LucasChat = lazy(() => import("./components/LucasChat"));
import useHomeState from "./useHomeState";
import useLucasAttachments from "./useLucasAttachments";
import useLucasConversations from "./useLucasConversations";
import { deriveSuggestions } from "./lucasSuggestions";
import { deriveTasks } from "./homeTasks";
import { mapBroadcastsToNotices } from "./homeNotices";

/**
 * Home v2 — the platform's front door.
 *
 * Replaces the implementation dashboard, which only had something to say while
 * onboarding was in progress and then froze on "Onboarding Concluído!"
 * permanently. Here the layout is fixed and the *content* adapts: the aurora
 * takes the account's colour, one alert can surface above the greeting, and
 * everything else sorts itself into the task list.
 *
 * The page commits to a dark treatment regardless of the app's theme toggle —
 * the aurora is the design, and it has no light-mode equivalent.
 *
 * Every state is reviewed against a real account in that state — there is no
 * scenario switcher, and no fixture path that can drift from what the platform
 * actually returns.
 */
export default function HomePage() {
  const { clientId: paramId } = useParams();
  const { user, billingPaymentLink } = useAuth();
  const { isEmbed } = useEmbed();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { on: onSocket } = useSocket();
  const clientId = paramId || user?.accountId;

  const [noticesOpen, setNoticesOpen] = useState(false);
  const [dismissedTaskIds, setDismissedTaskIds] = useState([]);

  // ── Data ──────────────────────────────────────────────────────────────────
  const { data: onboarding } = useQuery({
    queryKey: ["home", "onboarding", clientId],
    enabled: !!clientId,
    staleTime: 60_000,
    queryFn: async () => {
      const res = await authFetch(
        `/api/internal/implementation/project/${clientId}/onboarding`,
      );
      if (!res.ok) throw new Error("Falha ao carregar onboarding");
      return res.json();
    },
  });

  const { data: editorial } = useQuery({
    queryKey: ["home", "platform-content"],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await authFetch(
        "/api/internal/platform-content?placement=DASHBOARD&limit=4",
      );
      if (!res.ok) throw new Error("Falha ao carregar conteúdo");
      return res.json();
    },
  });

  const { data: broadcastHistory } = useQuery({
    queryKey: ["home", "broadcast-history"],
    staleTime: 5 * 60_000,
    queryFn: () => getMyHistory({ page: 1, pageSize: 20 }),
  });

  /**
   * Recent send failures, used only to shape the shortcut row.
   *
   * Fetched once on mount and revalidated by event — never on a timer. The
   * platform already broadcasts `ticket:updated` to the account room carrying
   * the last message's status, so a failure is announced for free; polling
   * would add load to learn something we are already being told.
   */
  const { data: sendErrors } = useQuery({
    queryKey: ["home", "send-errors", clientId],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await authFetch("/api/internal/support/send-errors?limit=3");
      if (!res.ok) return { items: [] };
      return res.json();
    },
  });

  useEffect(() => {
    return onSocket("ticket:updated", (payload) => {
      const last = payload?.lastMessage;
      if (last?.direction !== "SENT") return;
      if (String(last?.status).toUpperCase() !== "FAILED") return;
      queryClient.invalidateQueries({ queryKey: ["home", "send-errors", clientId] });
    });
  }, [onSocket, queryClient, clientId]);

  // ── Derived state ─────────────────────────────────────────────────────────
  const { scenario, alert, billing, greeting, lucasRestriction } = useHomeState({
    onboarding,
    accountId: clientId,
  });

  const stepConfig = useMemo(() => buildStepConfig(clientId), [clientId]);

  const notices = useMemo(
    () => mapBroadcastsToNotices(broadcastHistory?.items || []),
    [broadcastHistory],
  );
  const unreadNotices = notices.filter((n) => !n.read).length;

  const tasks = useMemo(() => {
    const derived = deriveTasks({ scenario, billingPaymentLink, accountId: clientId });
    return derived.filter((task) => !dismissedTaskIds.includes(task.id));
  }, [scenario, billingPaymentLink, clientId, dismissedTaskIds]);

  // ── Lucas ─────────────────────────────────────────────────────────────────
  const {
    messages,
    isTyping,
    activity,
    isLoading,
    sendMessage,
    stopGeneration,
    uploadScreenshot,
    closeConversation,
    rateAnswer,
    requestHuman,
    notifyTyping,
    reactToMessage,
    handover,
  } = useSupportChatSession();
  // O estado de aberto vem do contexto porque o botão "Suporte" da sidebar
  // precisa abri-lo de fora. Todo o resto da conversa mora aqui.
  const {
    isOpen: chatOpen,
    open: openChat,
    close: closeChat,
    activate: activateChat,
  } = useSupportChatUI();
  const [question, setQuestion] = useState("");
  // O trecho que está sendo respondido. Só existe com atendimento humano ativo:
  // a citação vive no ticket do Inbox, e sem ticket não há o que citar.
  const [replyTo, setReplyTo] = useState(null);
  const attachments = useLucasAttachments({ upload: uploadScreenshot });
  const conversations = useLucasConversations({ messages });

  /**
   * Submit is what triggers the takeover — not typing.
   *
   * The order matters: the message is dispatched *before* the layout flips, so
   * `useSupportChat` has already appended the optimistic user bubble and turned
   * on `isLoading`. The conversation surface therefore mounts with the question
   * already in it and Lucas already "thinking", and the animation plays around
   * live content instead of revealing it afterwards.
   */
  const handleAsk = useCallback(
    (text) => {
      const trimmed = (text ?? question).trim();
      const ready = attachments.readyAttachments;
      if (!trimmed && !ready.length) return;

      // Escrever numa conversa encerrada cairia na ATIVA sem avisar.
      if (conversations.isReadOnly) return;

      setQuestion("");
      // Só imagem entra em `imageUrl`. Mandar o primeiro anexo qualquer fazia
      // um `.twflow.json` ser baixado pelo modelo de visão, que recusava com
      // 400 e derrubava o turno. O array vai completo — o servidor roteia cada
      // arquivo pelo tipo.
      sendMessage(trimmed, ready.find((a) => a.isImage)?.url, ready, replyTo);
      attachments.clear();
      // A citação vale para UMA mensagem. Deixá-la de pé grudaria a próxima
      // frase no mesmo trecho, sem ninguém ter pedido.
      setReplyTo(null);
      openChat();
    },
    [question, attachments, sendMessage, openChat, conversations.isReadOnly, replyTo],
  );

  const handleResolve = useCallback(() => {
    closeConversation();
    conversations.refetchConversations();
    closeChat();
  }, [closeConversation, conversations, closeChat]);

  /**
   * Não existe "criar conversa": `getOrCreateConversation` sempre devolve a
   * ativa, então uma nova só começa depois que a atual é encerrada — e é o
   * encerramento que gera o resumo que a torna legível na lista.
   */
  const handleNewChat = useCallback(() => {
    closeConversation();
    conversations.resumeActive();
    conversations.refetchConversations();
    openChat();
  }, [closeConversation, conversations, openChat]);

  // A restrição vence o chat aberto: a conta pode ser bloqueada durante a
  // conversa (o evento `billing:blocked` atualiza o status na hora), e manter o
  // painel aberto só produziria 402 no próximo envio.
  /**
   * Dois estados, não um.
   *
   * `lucasOpen` é a TELA do Lucas: sem os cards da home, com a lista de
   * conversas, aurora recolhida. `conversationOpen` é a conversa com conteúdo:
   * é ela que faz a área de mensagens crescer e o composer descer.
   *
   * Separar os dois é o que dá o estado vazio de graça — "Nova conversa" volta
   * a ser exatamente o layout de entrada, com o composer centralizado e os
   * atalhos, e a transição que já existe só acontece quando a primeira resposta
   * chega. Antes os dois eram a mesma variável, então uma conversa recém-criada
   * abria numa área de rolagem vazia.
   */
  const lucasOpen = chatOpen && !lucasRestriction;
  const shownMessages = conversations.isReadOnly
    ? conversations.viewedMessages
    : messages;
  const conversationOpen =
    lucasOpen && (shownMessages.length > 0 || isLoading || isTyping);

  // A conversa abre no topo da coluna: quem perguntou depois de rolar até os
  // cards veria a transição fora da viewport.
  //
  // Instantâneo de propósito. Rolagem suave roda com duração e easing próprios,
  // enquanto a altura da página muda por baixo dela — as duas animações brigam e
  // o resultado lê como travada. Um salto único ANTES do movimento é mais limpo
  // que dois movimentos disputando o mesmo eixo.
  useEffect(() => {
    if (lucasOpen) window.scrollTo({ top: 0, behavior: "auto" });
  }, [lucasOpen]);

  /**
   * Uma vez carregado, fica.
   *
   * Desmontar ao fechar devolveria o chunk ao cache do navegador mas perderia
   * a posicao de rolagem da conversa, e reabrir piscaria. O estado das
   * mensagens nao mora aqui (esta no provider), entao manter montado custa
   * apenas a arvore de baloes.
   */
  const [chatMounted, setChatMounted] = useState(false);
  useEffect(() => {
    if (lucasOpen) setChatMounted(true);
  }, [lucasOpen]);

  const suggestions = useMemo(
    () => deriveSuggestions({ sendErrors: sendErrors?.items || [] }),
    [sendErrors],
  );

  // ── Navigation helpers ────────────────────────────────────────────────────
  const openHref = useCallback(
    (href) => {
      if (!href) return;
      if (/^https?:\/\//.test(href)) {
        window.open(href, "_blank", "noopener,noreferrer");
        return;
      }
      navigate(href);
    },
    [navigate],
  );

  const handleOpenArticle = useCallback(
    (item) => {
      if (item.href) return openHref(item.href);
      return navigate(`/internal/support/article/${item.id}`);
    },
    [navigate, openHref],
  );

  const showChecklist = scenario === "onboarding" && !!onboarding;

  return (
    /* Com a conversa aberta a página inteira cabe na viewport, então ela não
       deve rolar: quem rola é a conversa. Sem isto sobram duas barras, e a de
       fora move o composer para fora da tela. `h-full` prende à altura do
       `<main>` do layout, que é onde a rolagem da página vive. */
    <div
      className={`flex w-full flex-col bg-[#f7f8fc] font-jakarta text-[#15161f] dark:bg-[#0a0a0f] dark:text-white ${
        lucasOpen ? "h-full overflow-hidden" : "min-h-full"
      }`}
    >
      <div className="relative flex flex-1 flex-col overflow-hidden">
        <AuroraBackground scenario={scenario} dimmed={lucasOpen} />

        {/* Fora do fluxo de propósito: a coluna do chat não muda de estrutura
            quando a lista aparece, então a transição continua sendo a mesma. */}
        <aside
          aria-hidden={!lucasOpen}
          inert={lucasOpen ? undefined : ""}
          className={`absolute bottom-0 left-0 top-0 z-20 hidden w-[264px] flex-col px-4 py-6 transition-[opacity,transform] duration-[700ms] ease-[cubic-bezier(.25,.8,.35,1)] motion-reduce:transition-none lg:flex ${
            lucasOpen
              ? "translate-x-0 opacity-100"
              : "pointer-events-none -translate-x-4 opacity-0"
          }`}
        >
          <ConversationList
            conversations={conversations.conversations}
            activeLabel={conversations.activeLabel}
            viewingId={conversations.viewingId}
            hasActiveConversation={conversations.hasActiveConversation}
            onOpenConversation={conversations.openConversation}
            onResumeActive={conversations.resumeActive}
            onDeleteConversation={conversations.deleteConversation}
            onNewChat={handleNewChat}
          />
        </aside>

        {/* Mesmo canto nos dois estados. Com a conversa aberta o que importa
            ali é sair dela, não abrir avisos — e o botão fora da área de
            mensagens é o que permite ela não ter cabeçalho. */}
        <div className="absolute right-8 top-5 z-30">
          {lucasOpen ? (
            <button
              type="button"
              onClick={closeChat}
              aria-label="Fechar conversa"
              className="grid h-[34px] w-[34px] place-items-center rounded-full border border-black/10 bg-white/70 text-[#15161f] shadow-sm backdrop-blur-lg transition-colors hover:bg-white dark:border-white/[.16] dark:bg-[#0a0a0f]/50 dark:text-white dark:shadow-none dark:hover:bg-white/10"
            >
              <svg viewBox="0 0 16 16" aria-hidden="true" className="h-3.5 w-3.5">
                <path
                  d="M4 4l8 8M12 4l-8 8"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setNoticesOpen(true)}
              className="flex h-[34px] items-center gap-2 whitespace-nowrap rounded-[20px] border border-black/10 bg-white/70 px-[13px] text-xs font-semibold text-[#15161f] shadow-sm backdrop-blur-lg transition-colors hover:bg-white dark:border-white/[.16] dark:bg-[#0a0a0f]/50 dark:text-white dark:shadow-none dark:hover:bg-white/10"
            >
              Avisos
              {unreadNotices > 0 && (
                <span className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-lg bg-[#15161f] px-[5px] text-[9.5px] font-extrabold text-white dark:bg-white dark:text-[#0a0a0f]">
                  {unreadNotices}
                </span>
              )}
            </button>
          )}
        </div>

        {/* Hero + conversa.
            O composer é o ÚLTIMO filho desta coluna nos dois estados e nunca
            muda de lugar no DOM. Fechado, ele fica logo abaixo da saudação;
            aberto, a coluna vira altura de viewport e o chat cresce acima dele,
            então o composer termina no rodapé sem ser remontado nem animado à
            mão. É por isso que não há FLIP nem elemento compartilhado aqui. */}
        {/* A altura desta coluna NÃO é animada. `min-height` só empurra depois
            de ultrapassar a altura do conteúdo (~600px de saudação + caixa),
            então os primeiros 70% da transição não moviam nada e o resto vinha
            de uma vez — era esse o solavanco. Quem anima é a altura do chat,
            logo abaixo: ela é a grandeza que de fato desloca o composer. */}
        {/* A coluna é full-width para a barra de rolagem da conversa ficar na
            borda da página, não colada no texto. A largura de leitura é aplicada
            em cada bloco, não no container. */}
        {/* Três estados, não dois: conversa com conteúdo (composer no rodapé),
            Lucas vazio (composer centralizado na tela, como o layout de
            entrada), e a home. O vazio é de graça — é o mesmo bloco da home sem
            os cards, e a transição só entra quando a primeira resposta chega. */}
        {/* `lg:pl-[264px]` reserva a faixa da lista de conversas. A lista é
            `absolute`, então não empurra nada sozinha — sem esta reserva a
            coluna de leitura centraliza na viewport INTEIRA e passa por baixo
            dela, que é o balão invadindo a lista. */}
        <div
          className={`relative z-10 flex w-full flex-col transition-[padding] duration-[700ms] ease-[cubic-bezier(.25,.8,.35,1)] motion-reduce:transition-none ${
            lucasOpen ? "lg:pl-[264px]" : ""
          } ${
            conversationOpen
              ? "min-h-0 flex-1 pb-8 pt-16"
              : lucasOpen
                ? "flex-1 justify-center pb-8 pt-8 text-center"
                : "pb-[76px] pt-[104px] text-center"
          }`}
        >
          <div className="mx-auto w-full max-w-[760px] px-6">
            <Collapse open={lucasOpen && !conversationOpen}>
              <div className="pb-8">
                <LucasSpark />
              </div>
            </Collapse>

            <Collapse open={!conversationOpen}>
              <AlertPill alert={alert} />
              <h1 className="m-0 text-[34px] font-semibold leading-[1.2] tracking-[-.028em] animate-rise motion-reduce:animate-none">
                {greeting}
              </h1>
            </Collapse>
          </div>

          {/* Altura explícita em vez do truque de grid `0fr → 1fr`: aquele
              depende de o browser interpolar a unidade `fr`, e quando não
              interpola a altura SNAPA enquanto a opacidade faz fade em 700ms —
              lê exatamente como "um pulo, só que mais lento". `0 → calc(...)`
              são dois valores definidos e interpolam em qualquer lugar. */}
          {/* 262px é a soma exata da moldura em volta: pt-16 (64) + mt-7 (28) +
              composer (118) + disclaimer (20) + pb-8 (32). Errar esse número
              para menos estoura a viewport e devolve a barra de rolagem da
              página. `min-h-0` deixa este bloco encolher se o composer crescer
              com anexos, em vez de empurrar o rodapé para fora da tela. */}
          <div
            className={`min-h-0 shrink overflow-hidden transition-[height,opacity] duration-[700ms] ease-[cubic-bezier(.25,.8,.35,1)] motion-reduce:transition-none ${
              conversationOpen
                ? "h-[calc(100dvh-262px)] opacity-100"
                : "pointer-events-none h-0 opacity-0"
            }`}
          >
            {chatMounted && (
              <Suspense fallback={null}>
                <LucasChat
                  messages={
                    conversations.isReadOnly ? conversations.viewedMessages : messages
                  }
                  isTyping={!conversations.isReadOnly && isTyping}
                  isLoading={conversations.isLoadingViewed || (!conversations.isReadOnly && isLoading)}
                  activity={conversations.isReadOnly ? null : activity}
                  onResolve={handleResolve}
                  // Continua a conversa em vez de abrir outro canal: o Lucas
                  // registra o chamado com o contexto que já está em mãos, que é a
                  // única forma de contato que existe hoje. Um botão que promete
                  // transferência que não acontece seria pior que nenhum botão.
                  // O assunto vem do próprio handoff: sem ele o clique era mudo
                  // ("quero falar com uma pessoa sobre isso") e o turno seguinte
                  // recomeçava do zero, sem saber sobre o quê.
                  onHandoff={
                    conversations.isReadOnly
                      ? undefined
                      : (message) => handleAsk(message || "Quero falar com uma pessoa sobre isso.")
                  }
                  // Clicar num cartão de decisão continua a conversa: a opção
                  // carrega a mensagem que ela envia.
                  onPickOption={conversations.isReadOnly ? undefined : handleAsk}
                  // Socorro de verdade não vira outro turno com o modelo: abre
                  // atendimento no Inbox da TouchWorks e a conversa continua aqui.
                  // Quem decide por qual caminho vai é o backend, pelo `via`.
                  onRequestHuman={conversations.isReadOnly ? undefined : requestHuman}
                  // Conversa encerrada mostra o voto e não deixa mudar: ela é
                  // leitura, e um controle vivo ali prometeria uma edição que não
                  // acontece.
                  onRate={conversations.isReadOnly ? undefined : rateAnswer}
                  // Responder existe onde há ticket para citar — ou seja, com o
                  // atendimento humano em curso.
                  onReply={conversations.isReadOnly ? undefined : setReplyTo}
                  onReact={conversations.isReadOnly ? undefined : reactToMessage}
                  // O passo de menu é atalho: leva direto à tela, sem recarregar a
                  // aplicação e sem a pessoa varrer o menu atrás do nome.
                  onNavigate={navigate}
                  handover={conversations.isReadOnly ? null : handover}
                  readOnly={conversations.isReadOnly}
                />
              </Suspense>
            )}
          </div>

          <div className="mx-auto mt-7 w-full max-w-[760px] px-6">
            {conversations.isReadOnly ? (
              // Conversa encerrada é leitura. Escrever aqui cairia na ativa sem
              // avisar — o botão diz para onde a próxima mensagem vai.
              <div className="flex min-h-[118px] flex-col items-center justify-center gap-2 rounded-[20px] border border-dashed border-black/15 bg-white/50 p-5 text-center backdrop-blur-[22px] dark:border-white/[.16] dark:bg-[#0e0e14]/40">
                <p className="m-0 text-[13.5px] leading-[1.45] text-[#5c5f70] dark:text-white/60">
                  Esta conversa foi encerrada.
                </p>
                <button
                  type="button"
                  onClick={conversations.resumeActive}
                  className="inline-flex h-[34px] items-center whitespace-nowrap rounded-[17px] bg-[#15161f] px-4 text-xs font-bold text-white transition-opacity hover:opacity-90 dark:bg-white dark:text-[#0a0a0f]"
                >
                  Voltar para a conversa atual
                </button>
              </div>
            ) : lucasRestriction ? (
              <LucasRestricted restriction={lucasRestriction} />
            ) : (
              <LucasComposer
                onFocus={activateChat}
                value={question}
                onChange={(text) => {
                  setQuestion(text);
                  // Com atendimento humano em curso, o outro lado vê que a
                  // pessoa está escrevendo — como em qualquer conversa. Sem
                  // handover a chamada não faz nada.
                  notifyTyping();
                }}
                onSubmit={handleAsk}
                attachments={attachments.attachments}
                onAttach={attachments.add}
                onPaste={attachments.addFromClipboard}
                onRemoveAttachment={attachments.remove}
                replyTo={replyTo}
                onCancelReply={() => setReplyTo(null)}
                // Enquanto o Lucas responde, o botão de enviar vira parar. Não
                // vale na leitura de uma conversa encerrada, que não tem turno.
                busy={!conversations.isReadOnly && (isTyping || isLoading)}
                onStop={stopGeneration}
                // Foca sempre que a tela do Lucas está aberta, inclusive na
                // conversa nova — quem clicou em "Nova conversa" quer escrever.
                autoFocus={lucasOpen}
                placeholder={
                  conversationOpen ? "Pergunte outra coisa" : undefined
                }
                hint={
                  conversationOpen
                    ? undefined
                    : "Lucas responde com base na Central de Ajuda"
                }
              />
            )}
          </div>

          <div className="mx-auto w-full max-w-[760px] px-6">
            <Collapse open={!conversationOpen && !lucasRestriction}>
              <LucasSuggestions suggestions={suggestions} onPick={handleAsk} />
            </Collapse>
          </div>
        </div>

        {/* Body — sai de cena deslizando para baixo quando a conversa abre.
            EMB-13: no shell embutido este bloco inteiro nem monta — a spec só
            reserva Lucas e os avisos da plataforma para o Dashboard embutido;
            `Collapse` só esconde visualmente (aria-hidden/inert), nunca tira
            do DOM, então o corte precisa vir de FORA dele. */}
        {!isEmbed && (
        <Collapse open={!lucasOpen} delay={80}>
          <div className="relative z-10 mx-auto max-w-[1060px] px-8 pb-[76px]">
            {showChecklist && (
              <OnboardingChecklist onboarding={onboarding} stepConfig={stepConfig} />
            )}

            <EditorialCards
              items={editorial?.items || []}
              scenario={scenario}
              onOpen={handleOpenArticle}
            />

            <TaskList
              tasks={tasks}
              onDismiss={(task) =>
                setDismissedTaskIds((prev) => [...prev, task.id])
              }
              onAction={(task) => openHref(task.href)}
            />

            <BillingFooter billing={billing} />
          </div>
        </Collapse>
        )}
      </div>

      <NoticeDrawer
        open={noticesOpen}
        notices={notices}
        onClose={() => setNoticesOpen(false)}
        onOpenNotice={(notice) => {
          setNoticesOpen(false);
          navigate(`/internal/comunicados/${notice.id}`);
        }}
      />
    </div>
  );
}
